/**
 * State Manager - Handles candidate state reset and error recovery
 *
 * This module manages candidate state for reprocessing scenarios:
 * - Smart reset of only what changed (revenue, cash flows, etc.)
 * - Preservation of expensive calculations when possible
 * - Consistent error handling across all processing phases
 * - State validation and recovery
 */

import type { Candidate, CandidateResult, Configuration, Project } from '~/types';
import type { PreserveOptions, ProcessingResult, ResetOptions } from '../types';

export class StateManager {
  /**
   * Reset candidate state for reprocessing
   * Only resets what's specified in options - preserves expensive calculations when possible
   */
  static resetCandidateForReprocessing(candidate: Candidate, options: ResetOptions): void {
    // Always reset processing state
    candidate.isProcessed = false;

    if (options.resetRevenue) {
      // Reset revenue map if millage rates changed
      candidate.revenueMap = {};
    }

    if (options.resetCashFlows) {
      // Reset cash flows if bond series, dates, or revenue changed
      candidate.cashFlows = [];
    }

    if (options.resetResults !== false) {
      // Default to true
      // Reset results - always needed for reprocessing
      candidate.results = StateManager.createFreshResults();
    }

    if (options.resetDistribution) {
      // Reset distribution strategy if bond targeting changed
      // Note: This might need expansion based on future distribution logic
      if (candidate.revenueMap) {
        // Clear any distribution-specific data from revenue map
        Object.values(candidate.revenueMap).forEach((entry) => {
          entry.bondSeriesDetailsByBondId = {};
          entry.totalAccumulatedInterest = 0;
          entry.availableRevenue = entry.revenueMinusExistingDebt;
        });
      }
    }

    // Preserve specified values if requested
    if (options.preserve) {
      StateManager.preserveCalculatedValues(candidate, options.preserve);
    }
  }

  /**
   * Create fresh results object for a candidate
   */
  static createFreshResults(): CandidateResult {
    return {
      isViable: true,
      failureMessages: [],
      totalRevenue: 0,
      totalInterest: 0,
      totalPrincipal: 0,
      revenueUtilization: 0,
      efficiency: 0,
      interestRatio: 0,
      bondSeriesResults: [],
    };
  }

  /**
   * Preserve specific calculated values during reset
   * This is an optimization to avoid recalculating expensive operations
   */
  static preserveCalculatedValues(_candidate: Candidate, preserve: PreserveOptions): void {
    // For now, this is mostly placeholder - preservation logic can be added as needed
    // Examples of what could be preserved:

    if (preserve.millageShape) {
      // Keep millage shape calculations if only bond series changed
      // (millageInformation is already part of candidate, so it's preserved by default)
    }

    if (preserve.baseTimeline) {
      // Keep date/timeline calculations if only millage or bonds changed
      // (baseProjectElements.dateRecords are preserved by default)
    }

    if (preserve.existingDebt) {
      // Keep existing debt calculations if only millage or new bonds changed
      // (baseProjectElements.existingDebtInformation is preserved by default)
    }

    if (preserve.taxableValue) {
      // Keep taxable value projections if only bonds changed
      // (baseProjectElements.taxableValueMap is preserved by default)
    }
  }

  /**
   * Handle processing failures with consistent error management
   */
  static handleProcessingFailure(candidate: Candidate, error: unknown): ProcessingResult {
    const errorMessage = error instanceof Error ? error.message : String(error);

    // Mark candidate as processed but failed
    candidate.isProcessed = true;
    candidate.results.isViable = false;
    candidate.results.failureMessages = [`Processing failed: ${errorMessage}`];

    // Reset calculated values to prevent misleading data
    candidate.results.totalRevenue = 0;
    candidate.results.totalInterest = 0;
    candidate.results.totalPrincipal = 0;
    candidate.results.revenueUtilization = 0;
    candidate.results.interestRatio = 0;
    candidate.results.bondSeriesResults = [];

    return {
      success: false,
      candidate,
      error: errorMessage,
    };
  }

  /**
   * Validate candidate state before processing
   * Returns issues that would prevent successful processing
   */
  static validateCandidateForProcessing(
    candidate: Candidate,
    project: Project,
    configuration: Configuration
  ): {
    isValid: boolean;
    issues: string[];
  } {
    const issues: string[] = [];

    // Check required candidate data structures
    if (!candidate.millageInformation) {
      issues.push('Missing millage information');
    }

    // Check project data structures
    if (!project.dateElements?.dateRecords || project.dateElements.dateRecords.length === 0) {
      issues.push('Missing date records');
    }

    if (!project.taxableValueMap || Object.keys(project.taxableValueMap).length === 0) {
      issues.push('Missing taxable value map');
    }

    // Check configuration data structures
    if (!configuration.bondSeries || configuration.bondSeries.length === 0) {
      issues.push('Missing bond series');
    }

    if (!configuration.drfInputs) {
      issues.push('Missing DRF inputs');
    }

    return {
      isValid: issues.length === 0,
      issues,
    };
  }

  /**
   * Create a deep clone of a candidate for optimization scenarios
   * This ensures modifications don't affect the original candidate
   */
  static cloneCandidate(candidate: Candidate): Candidate {
    // Use JSON serialization for deep cloning
    // Note: This may need to be replaced with a more sophisticated cloning method
    // if candidates contain functions or other non-serializable data
    const cloned = JSON.parse(JSON.stringify(candidate));

    // Generate new ID for the cloned candidate
    // Use performance.now() for higher precision to avoid collisions
    cloned.id = `${candidate.id}-clone-${Math.floor(performance.now() * 1000)}-${crypto.randomUUID().slice(0, 8)}`;

    return cloned;
  }
}
